import type { ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface BottomSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** Required for screen readers; shown as the sheet heading. */
    title: ReactNode;
    /** Visually hide the title (still announced). */
    hideTitle?: boolean;
    description?: ReactNode;
    /** Trailing element on the header row. */
    headerAction?: ReactNode;
    /** Sticky footer (e.g. StickyActionBar content). */
    footer?: ReactNode;
    children: ReactNode;
    className?: string;
}

/**
 * Phone bottom sheet on Radix Dialog: focus trap, Esc and scrim-to-close come
 * from Radix. Drag handle, max 92dvh, internal scroll, safe-area padding.
 * Render it from phone trees only.
 */
export function BottomSheet({
    open,
    onOpenChange,
    title,
    hideTitle = false,
    description,
    headerAction,
    footer,
    children,
    className,
}: BottomSheetProps) {
    const { t } = useTranslation();
    return (
        <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-tg-ink/40 backdrop-blur-[2px]" />
                <DialogPrimitive.Content
                    {...(description ? {} : { 'aria-describedby': undefined })}
                    data-testid="mobile-bottom-sheet"
                    className={cn(
                        'fixed inset-x-0 bottom-0 z-[61] flex max-h-[92dvh] flex-col rounded-t-[20px] bg-white shadow-[var(--tg-shadow-pop)] focus:outline-none',
                        className,
                    )}
                >
                    <div className="flex justify-center pt-2.5" aria-hidden="true">
                        <span className="h-1.5 w-10 rounded-full bg-[#0F2A44]/15" />
                    </div>
                    <div className="flex min-h-12 items-center gap-2 pe-14 ps-4 pt-1">
                        <DialogPrimitive.Title className={cn('min-w-0 flex-1 truncate text-[17px] font-semibold text-[#0F2A44]', hideTitle && 'sr-only')}>
                            {title}
                        </DialogPrimitive.Title>
                        {headerAction}
                    </div>
                    {description ? (
                        <DialogPrimitive.Description className="px-4 text-[13px] text-[#64748B]">{description}</DialogPrimitive.Description>
                    ) : null}
                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4 pt-2">{children}</div>
                    {footer ? (
                        <div className="border-t border-[#0F2A44]/[0.06] bg-white px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3">{footer}</div>
                    ) : (
                        <div className="pb-[env(safe-area-inset-bottom)]" />
                    )}
                    <DialogPrimitive.Close
                        aria-label={t('common.close', 'Close')}
                        className="absolute end-2 top-3 grid h-11 w-11 place-items-center rounded-full text-[#0F2A44]/70 hover:bg-[#0F2A44]/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1FB7A6]"
                    >
                        <X className="h-5 w-5" aria-hidden="true" />
                    </DialogPrimitive.Close>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}
